import React, { useEffect, useState, useRef, useCallback } from 'react';
import ExternalLinkIcon from '../../assets/gptw_icons/Icons_rot/link-external-01.png';
import {
    Offcanvas,
    Form,
    OverlayTrigger,
    Tooltip,
    Row,
    Col,
    Button,
    Modal,
    Toast,
    ToastContainer,
    Spinner,
    Accordion,
    ListGroup,
    Badge,
} from 'react-bootstrap';
import TextareaAutosize from 'react-textarea-autosize';
import Select from 'react-select';
import { useDispatch, useSelector } from 'react-redux';
import { IconLock } from '@tabler/icons-react';

import { useFormState } from '../../hooks/useActionAreaState';
import { saveActionArea, deleteActionArea } from '../../features/actionAreasSlice';

import { selectTags, fetchTags } from '../../features/tagsSlice';

import FormField from '../common/FormField';
import FormDropdown from '../common/FormDropdown';

import './ActionAreaOffcanvas.css';

import AddTagIcon from '../../assets/gptw_icons/Icons_rot/tag-01.png';
import TrashIcon from '../../assets/gptw_icons/Icons_rot/trash-01.png';
import AddCalendarIcon from '../../assets/gptw_icons/Icons_rot/calendar-check-02.png';
import AddLoadingIcon from '../../assets/gptw_icons/Icons_rot/loading-01.png';
import InfoSquareIcon from '../../assets/gptw_icons/Icons_rot/info-square.png';

import MeasureOffcanvas from './MeasureOffcanvas';
import { createMeasure } from '../../features/measuresSlice';

import CustomOffcanvas from './CustomOffcanvas';
import './OffcanvasResponsive.css';

// Import the file upload widget
import FileUploadWidget from '../FileUploadWidget';
import { components } from 'react-select';

const getActionAreaStatusClass = (status) => {
    switch (status) {
        case 'nicht erreicht':
            return 'status-not-achieved';
        case 'erreicht':
            return 'status-achieved';
        case 'mehr als erreicht':
            return 'status-overachieved';
        default:
            return 'status-no-status';
    }
};

// Tag descriptions mapping
const TAG_DESCRIPTIONS = {
    'Ausgewogenheit': 'Ausgewogenheit misst den Grad, mit dem eine ausgewogene Behandlung aller Mitarbeitenden bei der Vergütung und sonstigen Leistungen vorliegt.',
    'Förderung': 'Förderung misst die Verfügbarkeit von Weiterbildungsmöglichkeiten, Ressourcen und Arbeitsmitteln sowie die Wertschätzung für gute Arbeitsleistung.',
    'Freundlichkeit': 'Freundlichkeit misst das Ausmaß, in dem die Mitarbeitenden das Arbeitsumfeld als einen freundlichen und einladenden Ort empfinden, an dem sie Spaß haben können und vom Miteinander profitieren.',
    'Fürsorge': 'Fürsorge misst, inwiefern Führungskräfte sich um das Wohlbefinden ihrer Mitarbeitenden sorgen, indem sie ein sicheres und gesundes Arbeitsumfeld ermöglichen und Mitarbeitenden Leistungen bieten, die sie auch außerhalb der Arbeit unterstützen. Fürsorglichen Führungskräften ist bewusst, welchen Einfluss die Arbeit auf das Privatleben ihrer Mitarbeitenden hat.',
    'Gemeinschaft': 'Gemeinschaft bezieht sich auf das Gefühl der Zugehörigkeit und des Zusammenhalts innerhalb der Organisation.',
    'Gerechtigkeit': 'Die Gerechtigkeit misst, inwieweit die Mitarbeitenden davon ausgehen, dass das Management integratives Verhalten fördert, Diskriminierung vermeidet und sich für einen angemessenen Umgang mit Beschwerden einsetzt.',
    'Integrität': 'Integrität misst, inwiefern Führungskräfte sich ambitionierten Werten verschreiben und Maßnahmen ergreifen, die auf diesen Werten beruhen.',
    'Kommunikation': 'Kommunikation misst, inwieweit die Kommunikation zwischen Führungskräften und Mitarbeitenden effektiv ist und zu einem wechselseitigen Dialog führt.',
    'Kompetenz': 'Kompetenz misst die Kompetenzen des Managements, die Organisation und ihre Mitarbeitenden gut zu führen.',
    'Neutralität': 'Neutralität misst, inwieweit Mitarbeitende der Meinung sind, dass Führungskräfte Bevorzugungen vermeiden sowie eine faire Bewertung von Personen in Bezug auf Stellen und Arbeitsaufgaben fördern.',
    'Persönlicher Beitrag': 'Der persönliche Beitrag misst, wie Mitarbeitende ihren eigenen Beitrag zur Organisation betrachten.',
    'Team': 'Team misst den Stolz, den die Mitarbeitenden auf ihr Team und die Leistungen des Teams entwickeln, sowie ihre Bereitschaft mehr für den Erfolg des Teams zu leisten.',
    'Unternehmensimage': 'Unternehmensimage erfasst, wie die Mitarbeitenden die Organisation und ihren Ruf in der Öffentlichkeit sehen.',
    'Vertrautheit': 'Vertrautheit misst, inwiefern Mitarbeitende gerne mit ihren Kollegen zusammenarbeiten, ein Gefühl haben, ganz "sie selbst" sein zu können, und sich aufeinander verlassen können bzw. sich gegenseitig unterstützen.',
    'Zusammenarbeit': 'Zusammenarbeit misst, wie Mitarbeitende das Bemühen der Führungskräfte wahrnehmen, die Mitarbeitende zur Kooperation anzuregen.',
    'Zusammengehörigkeit': 'Zusammengehörigkeit bezieht sich auf den Gemeinschaftssinn, der innerhalb einer Gruppe entwickelt wird, und misst das Ausmaß, in dem die Mitarbeitenden der Meinung sind, dass es ein Gefühl von „Familie" oder „Teamgeist" gibt.',
};

// Custom Option component for react-select with info icon tooltip
const CustomTagOption = (props) => {
    const { data } = props;
    const description = TAG_DESCRIPTIONS[data.label];

    return (
        <components.Option {...props}>
            <div className="d-flex align-items-center justify-content-between w-100">
                <span>{data.label}</span>
                {description && (
                    <OverlayTrigger
                        placement="left"
                        overlay={
                            <Tooltip id={`tag-tooltip-${data.id}`}>
                                {description}
                            </Tooltip>
                        }
                    >
                        <img
                            src={InfoSquareIcon}
                            alt="Info"
                            style={{
                                marginLeft: '8px',
                                cursor: 'pointer',
                                width: '16px',
                                height: '16px',
                                flexShrink: 0
                            }}
                            onClick={(e) => e.stopPropagation()}
                        />
                    </OverlayTrigger>
                )}
            </div>
        </components.Option>
    );
};

const ActionAreaOffcanvas = ({
    showOffcanvas,
    handleCloseOffcanvas,
    actionAreaData,
    handleActionAreaDataChange,
    openToResults = false,
    isUnitLeader = true,
    unitName,
}) => {
    const dispatch = useDispatch();
    const tags = useSelector(selectTags);
    const unitId = actionAreaData?.unit;
    const ergebnisteilRef = useRef(null);
    const offcanvasBodyRef = useRef(null);
    const shouldScrollToResults = useRef(openToResults);

    // Store the original action area data when offcanvas opens to enable discard functionality
    const initialActionAreaDataRef = useRef(null);

    const availableTags = tags.map(tag => ({
        value: tag.tag_name,
        label: tag.tag_name,
        id: tag.id
    }));

    const {
        formValues,
        isDirty,
        setIsDirty,
        handleChange,
        handleTagChange,
        handleSelect,
        handleTargetSelect,
    } = useFormState(actionAreaData, handleActionAreaDataChange, isUnitLeader);

    // Local state to control the show/hide animation
    const [show, setShow] = useState(showOffcanvas);

    useEffect(() => {
        dispatch(fetchTags());
    }, [dispatch]);

    // Synchronize local show state with prop and capture initial data
    useEffect(() => {
        setShow(showOffcanvas);
        // Update the ref when openToResults prop changes
        shouldScrollToResults.current = openToResults;
        
        // Capture the initial action area data when offcanvas opens
        // This allows us to restore it if user discards changes
        // Only capture once when opening (initialActionAreaDataRef.current is null)
        if (showOffcanvas && initialActionAreaDataRef.current === null) {
            initialActionAreaDataRef.current = JSON.parse(JSON.stringify(actionAreaData));
        }
        
        // Reset the ref when offcanvas closes
        if (!showOffcanvas) {
            initialActionAreaDataRef.current = null;
        }
    }, [showOffcanvas, openToResults]); // Intentionally not including actionAreaData to prevent overwriting

    // Handle scroll to results when offcanvas animation completes
    const handleEntered = useCallback(() => {
        if (shouldScrollToResults.current && ergebnisteilRef.current && offcanvasBodyRef.current) {
            // Calculate the position of the element relative to the scrollable container
            const container = offcanvasBodyRef.current;
            const element = ergebnisteilRef.current;
            const elementTop = element.offsetTop;
            
            // Scroll the offcanvas body to the element
            container.scrollTo({
                top: elementTop,
                behavior: 'smooth'
            });
            
            // Reset the flag after scrolling
            shouldScrollToResults.current = false;
        }
    }, []);

    useEffect(() => {
        if (actionAreaData.ends_at) {
            const isExpired = new Date(actionAreaData.ends_at) < new Date();
            setOpenAccordion(isExpired);
        }
    }, [actionAreaData.ends_at]);

    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [showTagRequiredModal, setShowTagRequiredModal] = useState(false);
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [toastVariant, setToastVariant] = useState('success');

    const [openAccordion, setOpenAccordion] = useState(false);
    const [isAddingMeasure, setIsAddingMeasure] = useState(false);

    /**
     * Check if the action area has any meaningful content entered by the user.
     * If any content has been added, a tag is required.
     */
    const hasContentWithoutTag = useCallback(() => {
        // Fields that indicate the user has started working on the action area
        const contentFields = [
            'actionarea_title',
            'description',
            'examples',
            'goal',
            'fixed_goal',
            'measure',
            'learning1',
            'learning2',
            'learning3',
            'status',
            'ends_at',
        ];

        // Check if any content field has a non-empty value
        const hasContent = contentFields.some(field => {
            const value = formValues[field];
            return value && value.toString().trim() !== '' && value !== 'Status wählen';
        });

        // Check if tags are empty
        const hasTags = formValues.tags && formValues.tags.length > 0;

        return hasContent && !hasTags;
    }, [formValues]);

    const handleHide = () => {
        // Check if there's content but no tag - show warning modal
        if (isDirty && hasContentWithoutTag()) {
            setShowTagRequiredModal(true);
            return;
        }

        // Save any pending changes before closing
        if (isDirty) {
            // Create a complete updated action area object
            const updatedActionArea = {
                ...actionAreaData,
                ...formValues
            };

            // Update the parent component with changes
            handleActionAreaDataChange(updatedActionArea);
            setIsDirty(false);
        }

        setShow(false); // This will trigger offcanvas to begin closing
    };

    /**
     * Handle discarding changes when tag is required but missing
     * Restores the original action area data before closing
     */
    const handleDiscardChanges = () => {
        setShowTagRequiredModal(false);
        setIsDirty(false);
        
        // Restore the original action area data to the parent component
        // This ensures that when the offcanvas closes, the parent won't save the modified data
        if (initialActionAreaDataRef.current) {
            handleActionAreaDataChange(initialActionAreaDataRef.current);
        }
        
        setShow(false);
    };

    /**
     * Handle going back to add a tag
     */
    const handleAddTagAndContinue = () => {
        setShowTagRequiredModal(false);
        // User stays in the offcanvas to add a tag
    };

    const handleExited = () => {
        handleCloseOffcanvas();
    };

    const handleDeleteClick = () => {
        if (!isUnitLeader) return;
        setShowConfirmModal(true);
    };

    const handleConfirmDelete = async () => {
        if (!isUnitLeader) return;

        try {
            await dispatch(deleteActionArea(actionAreaData.id)).unwrap();
            setToastMessage('Action area deleted successfully.');
            setToastVariant('success');
            setShowToast(true);
            setShow(false);
        } catch (error) {
            setToastMessage(`Failed to delete action area: ${error}`);
            setToastVariant('danger');
            setShowToast(true);
        } finally {
            setShowConfirmModal(false);
        }
    };

    const handleCancelDelete = () => {
        setShowConfirmModal(false);
    };

    const handleAddMeasure = async () => {
        if (!isUnitLeader) return;

        setIsAddingMeasure(true);
        try {
            const defaultMeasure = { measure_title: '' };
            const created = await dispatch(
                createMeasure({ actionAreaId: actionAreaData.id, measure: defaultMeasure })
            ).unwrap();
            openMeasureOffcanvas(created.measure);
        } catch (error) {
            console.error('Error creating measure:', error);
        } finally {
            setIsAddingMeasure(false);
        }
    };

    const selectedTagOptions = formValues.tags
        ? formValues.tags.map(tag => ({
            value: tag.tag_name,
            label: tag.tag_name,
            id: tag.id
        }))
        : [];

    const [selectedMeasure, setSelectedMeasure] = useState(null);
    const openMeasureOffcanvas = (measure) => {
        setSelectedMeasure(measure);
    };

    return (
        <>
            <CustomOffcanvas
                show={show}
                onHide={handleHide}
                onExited={handleExited}
                onEntered={handleEntered}
                placement="end"
                className="responsive-offcanvas"
            >
                <Offcanvas.Header closeButton style={{ paddingBottom: '0px' }}>
                    <div className="w-100">
                        <Row className="mb-3">
                            <Col className="d-flex justify-content-between align-items-center">
                                {isUnitLeader ? (
                                    <OverlayTrigger
                                        placement="right"
                                        overlay={<Tooltip id="delete-tooltip">Handlungsfeld löschen?</Tooltip>}
                                    >
                                        <Button variant="link" onClick={handleDeleteClick} className="p-0">
                                            <img
                                                src={TrashIcon}
                                                alt="Delete"
                                                style={{ width: '24px', height: '24px' }}
                                            />
                                        </Button>
                                    </OverlayTrigger>
                                ) : (
                                    <div className="d-flex align-items-center">
                                        <IconLock size={18} stroke={1.5} className="text-secondary" />
                                        <span className="ms-2 text-secondary">Nur Leserechte</span>
                                    </div>
                                )}
                                {unitName && (
                                    <Badge bg="secondary" className="ms-auto">
                                        {unitName}
                                    </Badge>
                                )}
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <Form.Label className="mb-0 text-muted fw-bold">Titel des Handlungsfelds</Form.Label>
                                <TextareaAutosize
                                    minRows={1}
                                    className="invisible-input form-control"
                                    name="actionarea_title"
                                    value={formValues.actionarea_title || ''}
                                    onChange={handleChange}
                                    placeholder="Titel des Handlungsfelds"
                                    style={{
                                        paddingBottom: '0',
                                        paddingTop: '0',
                                        backgroundColor: 'transparent',
                                        resize: 'none',
                                        fontSize: '2rem',
                                        fontWeight: 'bold',
                                        cursor: isUnitLeader ? 'text' : 'default',
                                        opacity: isUnitLeader ? 1 : 0.85,
                                        color: isUnitLeader ? 'inherit' : '#495057',
                                    }}
                                    readOnly={!isUnitLeader}
                                />
                            </Col>
                        </Row>
                    </div>
                </Offcanvas.Header>
                <Offcanvas.Body ref={offcanvasBodyRef} style={{ paddingTop: '0px', position: 'relative' }}>
                    <hr className="mt-0" />
                    <Form>
                        <FormField
                            label="Problembeschreibung- Was läuft nicht gut?"
                            name="description"
                            value={formValues.description}
                            onChange={handleChange}
                            tooltip={
                                <>
                                    Ziel: Die Herausforderung im Handlungsfeld klar benennen und eingrenzen.
                                </>
                            }
                            placeholder="Welche konkrete Problematik soll gelöst oder verbessert werden?  Wer ist betroffen?"
                            readOnly={!isUnitLeader}
                        />

                        {/* Tags Field */}
                        <Form.Group controlId="formTags" className="mb-3 d-flex align-items-center justify-content-between w-100">
                            <Form.Label className="me-2 w-20 d-flex align-items-center">
                                Schlagwörter
                                <OverlayTrigger
                                    placement="top"
                                    overlay={
                                        <Tooltip id="tags-tooltip">
                                            Nutze ein oder mehrere der vorgegebenen Schlagwörter, um Dein ausgewähltes Handlungsfeld und die dahinter liegende Problemstellung zu kategorisieren.
                                        </Tooltip>
                                    }
                                >
                                    <img
                                        src={InfoSquareIcon}
                                        alt="Info"
                                        style={{ marginLeft: '8px', cursor: 'pointer', width: '20px', height: '20px' }}
                                    />
                                </OverlayTrigger>
                            </Form.Label>
                            {isUnitLeader ? (
                                <Select
                                    isMulti
                                    name="tags"
                                    options={availableTags}
                                    value={selectedTagOptions}
                                    className="basic-single-select flex-grow-1 w-80"
                                    classNamePrefix="select"
                                    onChange={handleTagChange}
                                    isClearable
                                    components={{ Option: CustomTagOption }}
                                    styles={{
                                        control: (provided) => ({
                                            ...provided,
                                            maxWidth: '100%',
                                            borderRadius: 0,
                                            borderColor: 'hsl(0, 0%, 80%)',
                                            boxShadow: 'none',
                                            '&:hover': {
                                                borderColor: '#FF1628',
                                                boxShadow: '0 0 0 .25rem rgba(255, 22, 40, 0.25)',
                                            },
                                        }),
                                        menu: (provided) => ({ ...provided, borderRadius: 0 }),
                                        option: (provided) => ({
                                            ...provided,
                                            padding: '8px 12px',
                                        }),
                                    }}
                                    theme={(theme) => ({
                                        ...theme,
                                        colors: {
                                            ...theme.colors,
                                            primary: '#FF1628',
                                            primary25: '#EDF1EF',
                                            primary50: '#DEE5E4',
                                        },
                                    })}
                                />
                            ) : (
                                <div className="flex-grow-1 w-80 p-2" style={{
                                    backgroundColor: '#f8f9fa',
                                    border: '1px solid #ced4da',
                                    borderRadius: '.25rem'
                                }}>
                                    {selectedTagOptions.length > 0 ?
                                        selectedTagOptions.map(tag => (
                                            <Badge key={tag.id} bg="secondary" className="me-1">
                                                {tag.label}
                                            </Badge>
                                        ))
                                        : <span className="text-muted">Keine Schlagwörter ausgewählt</span>
                                    }
                                </div>
                            )}
                        </Form.Group>

                        <hr />

                        <FormField
                            label="Alltagsbeispiele - Wo tritt das Problem konkret auf?"
                            name="examples"
                            value={formValues.examples}
                            onChange={handleChange}
                            tooltip={
                                <>
                                    Ziel: Mit echten Beispielen greifbar machen, wie sich das Problem im Arbeitsalltag zeigt.
                                </>
                            }
                            placeholder="Welche Alltagsbeispiele veranschaulichen die erlebte Problematik?"
                            readOnly={!isUnitLeader}
                        />

                        <FormField
                            label="Wünschenswerter Zustand - Wie wäre es idealerweise?"
                            name="goal"
                            value={formValues.goal}
                            onChange={handleChange}
                            tooltip={<>Ziel: Einen motivierenden, positiven Zukunftszustand entwerfen.</>}
                            placeholder="Wie sähe der ideale Zustand aus, wenn alles rund läuft? Was wäre möglich, was aktuell nicht geht?"
                            readOnly={!isUnitLeader}
                        />

                        <FormField
                            label="Konkretes Ziel - Was genau soll erreicht werden?"
                            name="fixed_goal"
                            value={formValues.fixed_goal}
                            onChange={handleChange}
                            tooltip={<>Ziel: Ein klar umrissenes, realistisches Ergebnis fomulieren, das erreichbar ist.</>}
                            placeholder="Durch welches Ergebnis kommen wir dem gewünschten Zustand näher? Was möchten wir konkret erreichen?"
                            readOnly={!isUnitLeader}
                        />

                        <FormField
                            label="Erfolgskriterien - Woran erkennen wir den Fortschritt?"
                            name="measure"
                            value={formValues.measure}
                            onChange={handleChange}
                            tooltip={<>Ziel: Kriterien definieren, an denen die Zielerreichung objektiv oder subjektiv überprüfbar wird.</>}
                            placeholder="An welchen Kriterien wird sich erkennen lassen, dass das Ziel erreicht wurde? Was beobachten, messen oder hören wir, wenn es besser läuft?"
                            readOnly={!isUnitLeader}
                        />

                        <hr />

                        <Form.Group
                            controlId="formDate"
                            className="mb-3 d-flex align-items-center justify-content-between w-100"
                        >
                            <Form.Label className="w-50">
                                <img className="label-icon" src={AddCalendarIcon} alt="Calendar" />
                                Bis wann soll das Ziel erreicht sein?
                            </Form.Label>
                            <Form.Control
                                className="w-50"
                                type="date"
                                name="ends_at"
                                value={formValues.ends_at || ''}
                                onChange={handleChange}
                                readOnly={!isUnitLeader}
                                style={{
                                    backgroundColor: isUnitLeader ? '#fff' : '#f8f9fa',
                                    cursor: isUnitLeader ? 'text' : 'default',
                                }}
                            />
                        </Form.Group>

                        <hr />

                        {/* File Upload Widget */}
                        <Form.Group controlId="formUpload" className="mb-3">
                            <Form.Label className="d-flex align-items-center">
                                Upload
                                <OverlayTrigger
                                    placement="top"
                                    overlay={
                                        <Tooltip id="upload-tooltip">
                                            Nutze den Uploadbutton, um ergänzendes Material hochzuladen – beispielsweise ein Protokoll eines Workshops.
                                        </Tooltip>
                                    }
                                >
                                    <img
                                        src={InfoSquareIcon}
                                        alt="Info"
                                        style={{ marginLeft: '8px', cursor: 'pointer', width: '20px', height: '20px' }}
                                    />
                                </OverlayTrigger>
                            </Form.Label>
                            {/* Pass isUnitLeader to FileUploadWidget */}
                            <FileUploadWidget
                                unitId={unitId}
                                actionAreaId={actionAreaData.id}
                                isReadOnly={!isUnitLeader}
                            />
                        </Form.Group>

                        <hr />
                        <Form.Text
                            ref={ergebnisteilRef}
                            className="fw-bold px-2 py-1 rounded-0"
                            style={{
                                color: 'var(--white)',
                                backgroundColor: 'var(--brand-red-ff1628)',
                                display: 'inline-block'
                            }}
                        >
                            Folgende Felder erst ausfüllen, wenn ein Ergebnis vorliegt.
                        </Form.Text>

                        <h5 className="fw-bold my-3">Ergebnisteil</h5>

                        <FormField
                            label="Zielerreichung - Was haben wir erreicht?"
                            name="learning1"
                            value={formValues.learning1}
                            onChange={handleChange}
                            tooltip={<>Ziel: Den Zielerreichungsgrad überprüfen auf Basis der definierten Erfolgskriterien.</>}
                            placeholder="Wurde das gesetzte Ziel in dem Handlungsfeld erreicht? Wie lässt sich das konkret belegen oder wahrnehmen?"
                            readOnly={!isUnitLeader}
                        />

                        {isUnitLeader ? (
                            <FormDropdown
                                label="Wurde das gesetzte Ziel in diesem Handlungsfeld erreicht?"
                                icon={AddLoadingIcon}
                                value={formValues.status || "Status wählen"}
                                options={["Status wählen", "mehr als erreicht", "erreicht", "nicht erreicht"]}
                                onSelect={(value) => {
                                    // If "Status wählen" is selected, reset to null
                                    const statusValue = value === "Status wählen" ? null : value;
                                    handleTargetSelect(statusValue, 'status');
                                    // Immediately mark as dirty to ensure changes are saved
                                    setIsDirty(true);
                                    // Also update the parent component directly
                                    handleActionAreaDataChange({ status: statusValue });
                                }}
                                toggleClassName={`action-area-status-dropdown ${getActionAreaStatusClass(formValues.status)}`}
                                menuClassName="action-area-status-menu"
                                itemClassName="action-area-status-option"
                            />
                        ) : (
                            <Form.Group className="mb-3">
                                <Form.Label>
                                    <img className="label-icon" src={AddLoadingIcon} alt="Status Icon" />
                                    Bewertung Ergebnis
                                </Form.Label>
                                <div className="p-2" style={{
                                    backgroundColor: '#f8f9fa',
                                    border: '1px solid #ced4da',
                                    borderRadius: '.25rem'
                                }}>
                                    {formValues.status || 'Kein Status'}
                                </div>
                            </Form.Group>
                        )}

                        <FormField
                            label="Weiterentwickeln - Wie geht es sinnvoll weiter?"
                            name="learning2"
                            value={formValues.learning2}
                            onChange={handleChange}
                            tooltip={<>Ziel: Erkenntnisse aus der Zielerreichung nutzen, um den Prozess bei Bedarf weiterzuführen oder zu vertiefen.</>}
                            placeholder="Müssen wir etwas nachschärfen? Braucht es neue oder angepasste Maßnahmen oder Ziele?"
                            readOnly={!isUnitLeader}
                        />

                        <FormField
                            label="Reflexion des Prozesses - Wie lief der Weg hierhin?"
                            name="learning3"
                            value={formValues.learning3}
                            onChange={handleChange}
                            tooltip={<>Ziel: Lernen aus dem Vorgehen, um zukünftige Veränderungsprozesse besser zu gestalten.</>}
                            placeholder="Was haben wir aus dem zurückliegenden Prozess gelernt? Was hat gut funktioniert - was weniger? Was ist eine Empfehlung/ ein Wunsch fürs nächste Mal?"
                            readOnly={!isUnitLeader}
                        />

                        {actionAreaData?.measures?.length > 0 && (
                            <>
                                <hr />
                                <h5 className="fw-bold">Maßnahmen</h5>
                                <ListGroup variant="flush">
                                    {actionAreaData.measures.map((measure) => (
                                        <ListGroup.Item
                                            key={measure.id}
                                            className="d-flex align-items-center"
                                            style={{ cursor: 'pointer' }}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                openMeasureOffcanvas(measure);
                                            }}
                                        >
                                            <img
                                                src={ExternalLinkIcon}
                                                alt="Open"
                                                style={{ width: '16px', height: '16px', marginRight: '8px' }}
                                            />
                                            <div className="me-2">
                                                {measure.measure_title || "Maßnahme ohne Titel"}
                                            </div>
                                        </ListGroup.Item>
                                    ))}
                                    {isUnitLeader && (
                                        <Button
                                            variant="primary"
                                            onClick={handleAddMeasure}
                                            size="sm"
                                            className="mb-2"
                                            disabled={isAddingMeasure}
                                        >
                                            {isAddingMeasure ? (
                                                <>
                                                    <Spinner
                                                        as="span"
                                                        animation="border"
                                                        size="sm"
                                                        role="status"
                                                        aria-hidden="true"
                                                        className="me-2"
                                                    />
                                                    Hinzufügen...
                                                </>
                                            ) : (
                                                'Hinzufügen'
                                            )}
                                        </Button>
                                    )}
                                </ListGroup>
                            </>
                        )}
                    </Form>
                </Offcanvas.Body>
            </CustomOffcanvas>

            {selectedMeasure && (
                <MeasureOffcanvas
                    showOffcanvas={true}
                    handleCloseOffcanvas={() => setSelectedMeasure(null)}
                    measureId={selectedMeasure.id}
                    actionAreaId={actionAreaData.id}
                    unitId={actionAreaData.unit}
                />
            )}

            <Modal show={showConfirmModal} onHide={handleCancelDelete} centered>
                <Modal.Header closeButton>
                    <Modal.Title className="fw-bold">Löschvorgang bestätigen</Modal.Title>
                </Modal.Header>
                <Modal.Body>Möchten du das Handlungsfeld wirklich löschen?</Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCancelDelete}>Abbrechen</Button>
                    <Button onClick={handleConfirmDelete}>Löschen</Button>
                </Modal.Footer>
            </Modal>

            {/* Tag Required Warning Modal */}
            <Modal show={showTagRequiredModal} onHide={handleAddTagAndContinue} centered>
                <Modal.Header closeButton>
                    <Modal.Title className="fw-bold">Schlagwort erforderlich</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>
                        Du hast Inhalte in diesem Handlungsfeld hinzugefügt, aber noch kein Schlagwort ausgewählt.
                    </p>
                    <p>
                        <strong>Ein Schlagwort ist erforderlich</strong>, um die Änderungen zu speichern.
                    </p>
                    <p className="text-danger mb-0">
                        Wenn du fortfährst ohne ein Schlagwort hinzuzufügen, werden alle Änderungen verworfen.
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button className="tag-required-modal-btn tag-required-modal-btn--dark" onClick={handleDiscardChanges}>
                        Änderungen verwerfen
                    </Button>
                    <Button className="tag-required-modal-btn tag-required-modal-btn--primary" onClick={handleAddTagAndContinue}>
                        Schlagwort hinzufügen
                    </Button>
                </Modal.Footer>
            </Modal>

            <ToastContainer position="bottom-center" className="p-3">
                <Toast
                    onClose={() => setShowToast(false)}
                    show={showToast}
                    delay={3000}
                    autohide
                    bg={toastVariant}
                >
                    <Toast.Header>
                        <strong className="me-auto">
                            {toastVariant === 'success' ? 'Success' : 'Error'}
                        </strong>
                    </Toast.Header>
                    <Toast.Body className="text-white">{toastMessage}</Toast.Body>
                </Toast>
            </ToastContainer>
        </>
    );
};

export default ActionAreaOffcanvas;
